import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import { FctProveedor } from './fct/antd/FctProveedor';
import { AuthProvider } from './contexts/AuthContext';
import { PermissionProvider, usePermissions } from './contexts/PermissionContext';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import RequireResource from './components/Auth/RequireResource';
import MainLayout from './components/Layout/MainLayout';
import ExternalLayout from './components/Layout/ExternalLayout';
import HomePage from './pages/HomePage';
import TransmisionesPage from './pages/TransmisionesPage';
import TransmisionesAltaPage from './pages/TransmisionesAltaPage';
import { esEmbebido } from './embebido/puenteGdGo';
import OperationDetailPage from './pages/OperationDetailPage';
import KardioChatPage from './pages/KardioChatPage';
import AgentPage from './pages/AgentPage';
import ReconciliacionPage from './pages/ReconciliacionPage';
import BrevoSyncPage from './pages/BrevoSyncPage';
import BrevoSyncSistemasPage from './pages/BrevoSyncSistemasPage';
import MejoraCU2Page from './pages/MejoraCU2Page';
import VinculacionCU2Page from './pages/VinculacionCU2Page';
import AltasGestionPage from './pages/AltasGestionPage';
import AltasCasoPage from './pages/AltasCasoPage';
import ClientesGridPage from './pages/ClientesGridPage';
import ClientesDuplicadosPage from './pages/ClientesDuplicadosPage';
import SeguimientoFDocPage from './pages/SeguimientoFDocPage';
import SeguimientoPactosPage from './pages/SeguimientoPactosPage';
import EncuestasCxPage from './pages/EncuestasCxPage';
import OportunidadesPage from './pages/OportunidadesPage';
import FeriasPage from './pages/FeriasPage';
import EmpleadosPage from './pages/EmpleadosPage';
import ReportesListadoPage from './pages/ReportesListadoPage';
import VerificacionCaso from './components/Ferias/VerificacionCaso';
import ClienteCasoPagina from './components/Ferias/ClienteCasoPagina';
import AccionesCasoPagina from './components/Ferias/AccionesCasoPagina';
import './App.css';

// El visor de Power BI se carga al abrir un informe: powerbi-client es grande y solo lo usa esta ruta (#318).
const ReportesVisorPage = React.lazy(() => import('./pages/ReportesVisorPage'));

/**
 * Decide el layout de la bandeja Mejora CU2 según el tipo de usuario:
 * - Externo (Smartup): tiene GCvsc.MejoraCU2.Read pero NO GCvsc.Menu.Read → ExternalLayout
 *   (cabecera "Mejora CU2 · Smartup", sin menú interno).
 * - Interno (FCT): tiene GCvsc.Menu.Read → MainLayout con menú.
 * Es una ruta de nivel superior, fuera del árbol con MainLayout.
 */
const MejoraCU2Route: React.FC = () => {
  const { hasPermission, isLoading } = usePermissions();
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }
  const esExterno = !hasPermission('GCvsc.Menu.Read');
  const page = (
    <RequireResource resource="GCvsc.MejoraCU2.Read"><MejoraCU2Page /></RequireResource>
  );
  return esExterno
    ? <ExternalLayout>{page}</ExternalLayout>
    : <MainLayout>{page}</MainLayout>;
};

/**
 * El alta de transmisiones (#327) desde el navegador, con MainLayout. Embebida en GdGO! (`?embebido=1`, #326) no llega
 * aquí: App la pinta antes, sin MSAL.
 */
const AltaTransmisionesRoute: React.FC = () => (
  <MainLayout>
    <RequireResource resource="GCvsc.Transmisiones.Alta"><TransmisionesAltaPage /></RequireResource>
  </MainLayout>
);

/**
 * Ruta raíz: el Home interno para FCT; para usuarios externos (sin GCvsc.Menu.Read
 * pero con acceso a Mejora CU2) redirige a su bandeja, evitando el Home en blanco.
 */
const HomeRoute: React.FC = () => {
  const { hasPermission, isLoading } = usePermissions();
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <Spin size="large" />
      </div>
    );
  }
  if (!hasPermission('GCvsc.Menu.Read') && hasPermission('GCvsc.MejoraCU2.Read')) {
    return <Navigate to="/mejora-cu2" replace />;
  }
  return <HomePage />;
};

function App() {
  // Embebida en GdGO! (#326): sin login de MSAL ni menú. El token llega por el puente y la API comprueba el permiso.
  if (esEmbebido()) {
    return (
      <FctProveedor piel="interna">
        <TransmisionesAltaPage embebida />
      </FctProveedor>
    );
  }

  return (
    <FctProveedor piel="interna">
      <AuthProvider>
        <Router basename={process.env.PUBLIC_URL} future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <ProtectedRoute>
            <PermissionProvider>
              <Routes>
                {/* Mejora CU2: layout externo (Smartup) o MainLayout (FCT) según permisos (MCU2-T05) */}
                <Route path="/mejora-cu2" element={<MejoraCU2Route />} />
                {/* Alta de transmisiones (#327), desde el navegador; embebida en GdGO! (#326) se pinta antes, sin MSAL */}
                <Route path="/transmisiones/alta" element={<AltaTransmisionesRoute />} />
                {/* Reportes (#318, A-46): el visor de informes de Power BI, que sustituye a la aplicación Reportes del IIS.
                    A pantalla completa, sin MainLayout, como el original (#386). Recurso Reportes.Ver; el permiso de cada
                    informe lo decide la API. Sin entrada de menú: se llega por los enlaces de Gescom y del GdV (A-47, A-73). */}
                <Route path="/reportes/:nombre" element={
                  <RequireResource resource="Reportes.Ver">
                    <React.Suspense fallback={<Spin size="large" />}><ReportesVisorPage /></React.Suspense>
                  </RequireResource>
                } />
                {/* Modo normal: con MainLayout */}
                <Route path="/*" element={
                  <MainLayout>
                    <Routes>
                      <Route path="/" element={<HomeRoute />} />
                      <Route path="/transmisiones" element={
                        <RequireResource resource="GCvsc.Transmisiones.Read"><TransmisionesPage /></RequireResource>
                      } />
                      <Route path="/operations/:id" element={
                        <RequireResource resource="GCvsc.Transmisiones.Read"><OperationDetailPage /></RequireResource>
                      } />
                      <Route path="/chat" element={
                        <RequireResource resource="GCvsc.KardioChat.Read"><KardioChatPage /></RequireResource>
                      } />
                      <Route path="/agent" element={
                        <RequireResource resource="GCvsc.Agente.Read"><AgentPage /></RequireResource>
                      } />
                      <Route path="/reconciliacion" element={
                        <RequireResource resource="GCvsc.Reconciliacion.Read"><ReconciliacionPage /></RequireResource>
                      } />
                      <Route path="/vinculacion-cu2" element={
                        <RequireResource resource="GCvsc.Reconciliacion.Read"><VinculacionCU2Page /></RequireResource>
                      } />
                      <Route path="/brevo-sync" element={
                        <RequireResource resource="GCvsc.BrevoSync.Read"><BrevoSyncPage /></RequireResource>
                      } />
                      <Route path="/brevo-sync/sistemas" element={
                        <RequireResource resource="GCvsc.BrevoSync.Read"><BrevoSyncSistemasPage /></RequireResource>
                      } />
                      {/* F1.4 — Aplicación de validación de altas (GESCL-T01, tramo 5).
                          Sustituye a la UI mínima de T30 (AltasClientesPage, absorbida) y salda
                          la deuda de la ruta /altas sin RequireResource (RF-12): recurso
                          Clientes.Altas.Read (nomenclatura de dominio, D-GCVSC-013). */}
                      <Route path="/altas" element={
                        <RequireResource resource="Clientes.Altas.Read"><AltasGestionPage /></RequireResource>
                      } />
                      <Route path="/altas/:idCaso" element={
                        <RequireResource resource="Clientes.Altas.Read"><AltasCasoPage /></RequireResource>
                      } />
                      {/* F1.2 — Grid de clientes con alta directa (GESCL-T04). Recurso propio
                          Clientes.Grid.Read (spec §4). El alta directa exige además
                          Clientes.Altas.Gestionar — el mismo recurso de la gestión de altas y de
                          duplicados (mismos usuarios) —, que comprueba el backend. */}
                      <Route path="/clientes" element={
                        <RequireResource resource="Clientes.Grid.Read"><ClientesGridPage /></RequireResource>
                      } />
                      {/* Herramienta de eliminación de duplicados de cliente (GESCL-T07, tramo 5).
                          Un solo recurso para toda la herramienta, el mismo que gestiona altas
                          (D-GC-32): Clientes.Altas.Gestionar. Ruta hermana de /clientes, no anidada. */}
                      <Route path="/clientes/duplicados" element={
                        <RequireResource resource="Clientes.Altas.Gestionar"><ClientesDuplicadosPage /></RequireResource>
                      } />
                      {/* Seguimiento de documentos en firma digital (FDoc + Signaturit). Sin
                          RequireResource por el mismo motivo que /altas: su recurso ACL aún no
                          existe en el backend, y además la regla de visibilidad por rol sigue sin
                          cerrarse (ver SeguimientoFdocVisibilidad en el backend). Protegida solo
                          por ProtectedRoute (MSAL). */}
                      <Route path="/seguimiento-fdoc" element={<SeguimientoFDocPage />} />
                      {/* Seguimiento de pactos (Pacto de Firma por WhatsApp). SOLO Sistemas, por la ACL: recurso
                          Pactos.Seguimiento.Read (rol Sistemas, grupo «Dpto. Sistemas (s)»). El backend también
                          deniega con 403; esto es usabilidad, no el control de acceso. */}
                      <Route path="/seguimiento-pactos" element={
                        <RequireResource resource="Pactos.Seguimiento.Read"><SeguimientoPactosPage /></RequireResource>
                      } />
                      {/* Oportunidades (OPOR-I001): herramienta de Marketing. Un único recurso para
                          pantalla y API, Oportunidades.Gestionar (R-14, D-OP-28). */}
                      <Route path="/oportunidades" element={
                        <RequireResource resource="Oportunidades.Gestionar"><OportunidadesPage /></RequireResource>
                      } />
                      {/* Encuestas CX (CXCP_ENCUESTAS): panel interno de la campaña semanal y del
                          histórico de enviadas. Recurso Cx.Panel.Read, solo para los grupos
                          CxCp (s) y Dpto. Sistemas (s) (roles CxCp / Sistemas en la ACL). */}
                      <Route path="/encuestas-cx" element={
                        <RequireResource resource="Cx.Panel.Read"><EncuestasCxPage /></RequireResource>
                      } />
                      {/* Ferias (07_MODULOS/FERIAS): herramienta de Marketing. Reutiliza el recurso de
                          Clientes Clientes.Altas.Gestionar, en la pantalla y en la API (D-FE-12). */}
                      <Route path="/ferias" element={
                        <RequireResource resource="Clientes.Altas.Gestionar"><FeriasPage /></RequireResource>
                      } />
                      <Route path="/ferias/casos/:idCaso" element={
                        <RequireResource resource="Clientes.Altas.Gestionar"><VerificacionCaso /></RequireResource>
                      } />
                      <Route path="/ferias/casos/:idCaso/cliente" element={
                        <RequireResource resource="Clientes.Altas.Gestionar"><ClienteCasoPagina /></RequireResource>
                      } />
                      <Route path="/ferias/casos/:idCaso/acciones" element={
                        <RequireResource resource="Clientes.Altas.Gestionar"><AccionesCasoPagina /></RequireResource>
                      } />
                      {/* Reportes (#393): el listado de los informes que el usuario puede abrir, con menú. Cada uno se abre en
                          una pestaña nueva, en el visor a pantalla completa de /reportes/:nombre, que va fuera de MainLayout. */}
                      <Route path="/reportes" element={
                        <RequireResource resource="Reportes.Ver"><ReportesListadoPage /></RequireResource>
                      } />
                      {/* Empleados (07_MODULOS/EMPLEADOS): recurso Empleados.Ver; las acciones de gestión
                          las separa la propia pantalla (Empleados.Gestionar). */}
                      <Route path="/empleados" element={
                        <RequireResource resource="Empleados.Ver"><EmpleadosPage /></RequireResource>
                      } />
                    </Routes>
                  </MainLayout>
                } />
              </Routes>
            </PermissionProvider>
          </ProtectedRoute>
        </Router>
      </AuthProvider>
    </FctProveedor>
  );
}

export default App;
